import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, DatePicker, Drawer, Label, UniversalSelect } from '@feathr/components';

import type {
  ITransactionFilters,
  TDonationType,
  TTimeframePreset,
} from './TransactionFilters.types';
import { DEFAULT_FILTERS } from './TransactionFilters.types';
import { getActiveFilterCount } from './TransactionFilters.utils';

import * as styles from './TransactionFiltersDrawer.css';

interface IFormOption {
  id: string;
  name: string;
}

interface IProps {
  filters: ITransactionFilters;
  formOptions: IFormOption[];
  isOpen: boolean;
  onApply: (filters: ITransactionFilters) => void;
  onClose: () => void;
}

function TransactionFiltersDrawer({
  filters,
  formOptions,
  isOpen,
  onApply,
  onClose,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState<ITransactionFilters>(filters);

  const donationTypeOptions = useMemo(
    (): Array<{ id: TDonationType; name: string }> => [
      { id: 'one_time', name: t('One-time') },
      { id: 'recurring', name: t('Recurring') },
    ],
    [t],
  );

  const timeframeOptions = useMemo(
    (): Array<{ id: TTimeframePreset; name: string }> => [
      { id: 'all_time', name: t('All time') },
      { id: 'today', name: t('Today') },
      { id: 'last_7_days', name: t('Last 7 days') },
      { id: 'last_30_days', name: t('Last 30 days') },
      { id: 'last_90_days', name: t('Last 90 days') },
      { id: 'custom', name: t('Custom range') },
    ],
    [t],
  );

  // Sync local state when parent filters change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  function handleFormChange(selected: IFormOption[]): void {
    setLocalFilters((prev) => ({
      ...prev,
      formIds: selected.map((f) => f.id),
    }));
  }

  function handleTypeChange(selected: Array<{ id: TDonationType; name: string }>): void {
    setLocalFilters((prev) => ({
      ...prev,
      donationTypes: selected.map((item) => item.id),
    }));
  }

  function handleTimeframeChange(selected?: { id: TTimeframePreset; name: string }): void {
    setLocalFilters((prev) => ({
      ...prev,
      timeframe: selected?.id ?? 'all_time',
    }));
  }

  function handleStartDateChange(date: Date | null): void {
    setLocalFilters((prev) => ({
      ...prev,
      customDateRange: { ...prev.customDateRange, start: date },
    }));
  }

  function handleEndDateChange(date: Date | null): void {
    setLocalFilters((prev) => ({
      ...prev,
      customDateRange: { ...prev.customDateRange, end: date },
    }));
  }

  function handleClear(): void {
    setLocalFilters(DEFAULT_FILTERS);
  }

  function handleApply(): void {
    onApply(localFilters);
    onClose();
  }

  function handleClose(): void {
    setLocalFilters(filters);
    onClose();
  }

  const activeCount = getActiveFilterCount(localFilters);
  const selectedForms = formOptions.filter((f) => localFilters.formIds.includes(f.id));
  const selectedTypes = donationTypeOptions.filter((option) =>
    localFilters.donationTypes.includes(option.id),
  );
  const selectedTimeframe = timeframeOptions.find((option) => option.id === localFilters.timeframe);

  return (
    <Drawer
      isOpen={isOpen}
      name={'transaction-filters-drawer'}
      onClose={handleClose}
      title={t('Filters')}
    >
      <Drawer.Content addVerticalGap={true}>
        <div className={styles.filters}>
          <div>
            <Label>{t('Form')}</Label>
            <UniversalSelect<IFormOption>
              isMulti={true}
              name={'form-filter'}
              onChange={handleFormChange}
              options={formOptions}
              placeholder={t('All forms')}
              value={selectedForms}
            />
          </div>

          <div>
            <Label>{t('Type')}</Label>
            <UniversalSelect<{ id: TDonationType; name: string }>
              isMulti={true}
              name={'type-filter'}
              onChange={handleTypeChange}
              options={donationTypeOptions}
              placeholder={t('All types')}
              value={selectedTypes}
            />
          </div>

          <div>
            <Label>{t('Timeframe')}</Label>
            <UniversalSelect<{ id: TTimeframePreset; name: string }>
              isMulti={false}
              name={'timeframe-filter'}
              onChange={handleTimeframeChange}
              options={timeframeOptions}
              placeholder={t('All time')}
              value={selectedTimeframe}
            />
            {localFilters.timeframe === 'custom' && (
              <div className={styles.dateRange}>
                <div className={styles.dateRangeInputs}>
                  <DatePicker
                    label={t('Start date')}
                    onChange={handleStartDateChange}
                    placeholder={t('Start date')}
                    selected={localFilters.customDateRange.start}
                  />
                  <DatePicker
                    label={t('End date')}
                    onChange={handleEndDateChange}
                    placeholder={t('End date')}
                    selected={localFilters.customDateRange.end}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Drawer.Content>
      <Drawer.Actions className={styles.actions}>
        <Button className={styles.clearButton} name={'clear-filters'} onClick={handleClear}>
          {t('Clear ({{count}})', { count: activeCount })}
        </Button>
        <Button
          className={styles.applyButton}
          name={'apply-filters'}
          onClick={handleApply}
          type={'primary'}
        >
          {t('Apply')}
        </Button>
      </Drawer.Actions>
    </Drawer>
  );
}

export default TransactionFiltersDrawer;
